if (!customElements.get('lumecrin-product')) {
  class LumecrinProduct extends HTMLElement {
    static purchaseState(variant) {
      const priced = Number(variant?.price) > 0;
      return { priced, purchasable: priced && variant?.available === true };
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.events = { signal: this.controller.signal };
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.form = this.querySelector('.lp-form');
      this.variantSelect = this.querySelector('[data-variant-select]');
      this.panels = [...this.querySelectorAll('[data-media-panel]')];
      this.tabs = [...this.querySelectorAll('[data-gallery-tab]')];
      this.activeIndex = Math.max(0, this.panels.findIndex(panel => !panel.hidden));
      this.dialog = this.querySelector('[data-zoom-dialog]');
      this.zoomStage = this.querySelector('[data-zoom-stage]');
      this.mobileBar = this.querySelector('[data-mobile-bar]');
      this.quantity = this.querySelector('[data-quantity]');
      this.currentVariant = this.variants.find(variant => String(variant.id) === this.dataset.initialVariant);
      this.addEventListener('click', event => this.onClick(event), this.events);
      this.addEventListener('keydown', event => this.onKeydown(event), this.events);
      this.variantSelect.addEventListener('change', () => this.updateVariant(this.variantSelect.value, true), this.events);
      this.form.addEventListener('submit', event => this.onSubmit(event), this.events);
      this.dialog.addEventListener('close', () => this.afterZoomClose(), this.events);
      this.querySelector('.lp-gallery__stage')?.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch') this.swipeStart = { x: event.clientX, y: event.clientY };
      }, this.events);
      this.querySelector('.lp-gallery__stage')?.addEventListener('pointerup', event => {
        if (!this.swipeStart || event.pointerType !== 'touch') return;
        const dx = event.clientX - this.swipeStart.x;
        const dy = event.clientY - this.swipeStart.y;
        this.swipeStart = null;
        if (Math.abs(dx) > 55 && Math.abs(dy) < Math.abs(dx) * 0.55) {
          this.selectMedia(this.activeIndex + (dx < 0 ? 1 : -1));
        }
      }, this.events);
      window.addEventListener('pageshow', () => this.updateVariant(this.variantSelect.value, false), this.events);
      this.updateVariant(this.variantSelect.value, false);
    }

    disconnectedCallback() {
      if (this.dialog?.open) {
        this.dialog.close();
        this.afterZoomClose();
      }
      this.controller?.abort();
      this.purchaseObserver?.disconnect();
      this.initialized = false;
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : event.target.parentElement;
      const tab = target.closest('[data-gallery-tab]');
      if (tab) this.selectMedia(this.tabs.indexOf(tab));
      if (target.closest('[data-gallery-prev]')) this.selectMedia(this.activeIndex - 1);
      if (target.closest('[data-gallery-next]')) this.selectMedia(this.activeIndex + 1);
      const zoom = target.closest('[data-zoom]');
      if (zoom && typeof this.dialog.showModal === 'function') {
        event.preventDefault();
        this.zoomOpener = zoom;
        this.openZoom();
      }
      if (target.closest('[data-zoom-close]')) this.dialog.close();
      if (target.closest('[data-zoom-prev]')) this.stepZoom(-1);
      if (target.closest('[data-zoom-next]')) this.stepZoom(1);
      if (target.closest('[data-zoom-toggle]') || target.closest('[data-zoom-stage] img')) this.toggleZoomSize();
      const quantityButton = target.closest('[data-quantity-change]');
      if (quantityButton) {
        const direction = Number(quantityButton.dataset.quantityChange);
        if (direction > 0) this.quantity.stepUp();
        else this.quantity.stepDown();
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    onKeydown(event) {
      if (this.dialog.open && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        this.stepZoom(event.key === 'ArrowRight' ? 1 : -1);
        return;
      }
      const tab = event.target.closest?.('[data-gallery-tab]');
      if (!tab) return;
      let index = this.tabs.indexOf(tab);
      if (['ArrowRight', 'ArrowDown'].includes(event.key)) index += 1;
      else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) index -= 1;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.tabs.length - 1;
      else return;
      event.preventDefault();
      this.selectMedia(index, true);
    }

    selectMedia(index, focusTab = false) {
      if (!this.panels.length) return;
      const normalized = (index + this.panels.length) % this.panels.length;
      this.panels[this.activeIndex]?.querySelectorAll('video').forEach(video => video.pause());
      this.panels.forEach((panel, panelIndex) => { panel.hidden = panelIndex !== normalized; });
      this.tabs.forEach((tab, tabIndex) => {
        tab.setAttribute('aria-selected', String(tabIndex === normalized));
        tab.tabIndex = tabIndex === normalized ? 0 : -1;
      });
      this.activeIndex = normalized;
      this.querySelector('[data-gallery-current]').textContent = String(normalized + 1);
      if (focusTab) this.tabs[normalized]?.focus({ preventScroll: true });
      this.tabs[normalized]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      const zoomHint = this.querySelector('.lp-gallery__zoom-hint');
      if (zoomHint) zoomHint.hidden = this.panels[normalized].dataset.mediaType !== 'image';
    }

    updateVariant(id, announce) {
      const variant = this.variants.find(item => String(item.id) === String(id));
      if (!variant) return;
      this.currentVariant = variant;
      const state = LumecrinProduct.purchaseState(variant);
      this.querySelectorAll('[data-price-wrap]').forEach(element => { element.hidden = !state.priced; });
      this.querySelectorAll('[data-price]').forEach(element => { element.textContent = variant.formattedPrice; });
      const compare = this.querySelector('[data-compare-price]');
      compare.hidden = !state.priced || !(Number(variant.compareAtPrice) > variant.price);
      compare.textContent = variant.formattedCompareAtPrice || '';
      this.querySelector('[data-purchase-row]').hidden = !state.purchasable;
      this.querySelectorAll('[data-add-to-cart]').forEach(button => {
        button.disabled = !state.purchasable;
        button.hidden = !state.purchasable;
        button.querySelector('[data-add-label]').textContent = this.dataset.addLabel;
      });
      this.querySelectorAll('[data-enquiry]').forEach(link => { link.hidden = state.purchasable; });
      this.querySelector('[data-request-note]').hidden = state.priced;
      const availability = state.purchasable ? this.dataset.availableLabel : state.priced ? this.dataset.unavailableLabel : this.dataset.requestLabel;
      this.querySelector('[data-availability-text]').textContent = availability;
      this.querySelector('[data-availability]').dataset.state = state.purchasable ? 'available' : 'request';
      const min = Math.max(1, Number(variant.quantityMin) || 1);
      const step = Math.max(1, Number(variant.quantityStep) || 1);
      this.quantity.min = String(min);
      this.quantity.step = String(step);
      if (variant.quantityMax != null) this.quantity.max = String(variant.quantityMax);
      else this.quantity.removeAttribute('max');
      const quantity = Number(this.quantity.value);
      if (!Number.isFinite(quantity) || quantity < min || (quantity - min) % step !== 0 || (variant.quantityMax != null && quantity > variant.quantityMax)) this.quantity.value = String(min);
      if (announce) {
        this.querySelector('[data-variant-status]').textContent = `${state.priced ? `${variant.formattedPrice} · ` : ''}${availability}`;
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', currentUrl);
        if (variant.mediaId) {
          const mediaIndex = this.panels.findIndex(panel => panel.dataset.mediaId === String(variant.mediaId));
          if (mediaIndex !== -1) this.selectMedia(mediaIndex);
        }
      }
      this.observePurchase(state.purchasable);
    }

    observePurchase(purchasable) {
      this.purchaseObserver?.disconnect();
      if (!('IntersectionObserver' in window)) return;
      const target = purchasable ? this.querySelector('[data-purchase-row]') : this.form.querySelector('[data-enquiry]');
      if (!target) return;
      this.purchaseObserver = new IntersectionObserver(entries => {
        this.mobileBar.classList.toggle('is-hidden', entries[0].isIntersecting && entries[0].intersectionRatio >= 0.7);
      }, { threshold: [0, 0.7, 1] });
      this.purchaseObserver.observe(target);
    }

    onSubmit(event) {
      const state = LumecrinProduct.purchaseState(this.currentVariant);
      if (!state.purchasable) {
        event.preventDefault();
        this.querySelector('[data-variant-status]').textContent = this.dataset.requestLabel;
        return;
      }
      if (!this.quantity.checkValidity()) {
        event.preventDefault();
        this.quantity.reportValidity();
        this.querySelector('[data-variant-status]').textContent = this.dataset.invalidQuantityLabel;
        return;
      }
      this.querySelectorAll('[data-add-to-cart]').forEach(button => {
        button.disabled = true;
        button.querySelector('[data-add-label]').textContent = this.dataset.addingLabel;
      });
    }

    openZoom() {
      this.renderZoom();
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.querySelector('[data-zoom-close]').focus();
    }

    renderZoom() {
      const link = this.panels[this.activeIndex]?.querySelector('[data-zoom]');
      if (!link) return;
      const picture = new Image();
      picture.src = link.href;
      picture.alt = link.dataset.imageAlt;
      picture.decoding = 'async';
      this.zoomStage.replaceChildren(picture);
      this.zoomStage.classList.remove('is-enlarged');
      this.querySelector('[data-zoom-toggle]')?.setAttribute('aria-pressed', 'false');
    }

    stepZoom(direction) {
      const imageIndices = this.panels.map((panel, index) => panel.dataset.mediaType === 'image' ? index : -1).filter(index => index >= 0);
      if (!imageIndices.length) return;
      const currentPosition = imageIndices.indexOf(this.activeIndex);
      const nextPosition = (currentPosition + direction + imageIndices.length) % imageIndices.length;
      this.selectMedia(imageIndices[nextPosition]);
      this.renderZoom();
    }

    toggleZoomSize() {
      const enlarged = this.zoomStage.classList.toggle('is-enlarged');
      this.querySelector('[data-zoom-toggle]')?.setAttribute('aria-pressed', String(enlarged));
      if (!enlarged) this.zoomStage.scrollTo(0, 0);
    }

    afterZoomClose() {
      document.body.style.overflow = this.previousOverflow || '';
      const returnTarget = this.panels[this.activeIndex]?.querySelector('[data-zoom]') || this.tabs[this.activeIndex] || this.zoomOpener;
      if (returnTarget?.isConnected) returnTarget.focus({ preventScroll: true });
      this.zoomStage.replaceChildren();
    }
  }
  customElements.define('lumecrin-product', LumecrinProduct);
}
